import atlas from 'azure-maps-control';
import type { ReactNode } from 'react';
import { useContext, useEffect, useRef } from 'react';
import { AzureMapsContext, type IAzureMapPopupEvent, type IAzureMapsContextProps } from 'react-azure-maps';
import ReactDOM from 'react-dom';

interface InteractivePopupProps {
    children: ReactNode;
    isVisible?: boolean;
    options?: atlas.PopupOptions;
    events?: IAzureMapPopupEvent[];
}

const InteractivePopup = ({
                              children,
                              isVisible = true,
                              options,
                              events,
                          }: InteractivePopupProps) => {
    const { mapRef, isMapReady } = useContext<IAzureMapsContextProps>(AzureMapsContext);

    const popupContentRef = useRef<HTMLDivElement | null>(null);
    const popupInstanceRef = useRef<atlas.Popup | null>(null);

    if (!popupContentRef.current) {
        popupContentRef.current = document.createElement('div');
    }

    useEffect(() => {
        if (!mapRef || !isMapReady) return;
        if (popupInstanceRef.current) {
            mapRef.popups.remove(popupInstanceRef.current);
        }

        const popup = new atlas.Popup({
            ...options,
            content: popupContentRef.current ?? undefined,
            visible: isVisible,
        });

        mapRef.popups.add(popup);

        if (events && Array.isArray(events)) {
            events.forEach(({ eventName, callback }) => {
                mapRef.events.add(eventName, popup, callback);
            });
        }

        if (isVisible && options?.position) {
            popup.setOptions({ ...options, visible: true });
            popup.open(mapRef);
        } else {
            popup.close();
        }
        popupInstanceRef.current = popup;
        return () => {
            if (popupInstanceRef.current) {
                popupInstanceRef.current.close();
                mapRef.popups.remove(popupInstanceRef.current);
                popupInstanceRef.current = null;
            }
        };
    }, [mapRef, isMapReady, options, isVisible, events]);

    return popupContentRef.current
        ? ReactDOM.createPortal(children, popupContentRef.current)
        : null;
};

export default InteractivePopup;